import { createSelector } from 'reselect';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/index.tsx';
import type { ForgeUiBoardData } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { toProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	AGILITY_BOARD_TYPE,
	CMP_KANBAN_BOARD_TYPE,
	CMP_SCRUM_BOARD_TYPE,
} from '../../../common/constants/analytics.tsx';
import {
	SPRINT_STATE_ACTIVE,
	BACKLOG,
	KANBAN_BACKLOG_STRATEGY,
	SPRINT_BACKLOG_STRATEGY,
} from '../../../model/constants.tsx';
import type { CardListType, Project } from '../../../model/types.tsx';
import type { EntitiesState } from '../../reducers/entities/types.tsx';
import type { State } from '../../types.tsx';
import { getEntities } from '../entities/index.tsx';

// environment
export const getBaseUrl = (state: State) => state.config.baseUrl;
export const getLocale = (state: State) => state.config.locale;
export const getXsrfToken = (state: State) => state.config.xsrfToken;
export const getCloudId = (state: State) => state.config.cloudId;
export const getAtlassianAccountId = (state: State) => state.config.atlassianAccountId;
export const getIsCompanyManaged = (state: State) => state.config.isCompanyManaged;
export const getIsBoardCrossProject = (state: State) => state.config.isBoardCrossProject;
export const getDetailView = (state: State) => state.ui.detailView;

// identifiers
export const getRapidViewId = (state: State) => state.config.rapidViewId;
/*
 * @deprecated getProjectId getter should not be used for company-managed. Use projectIdSelector instead.
 * Eventually this will have to be deprecated for team-managed project as well.
 * */
const getProjectId = (state: State) => state.config.projectId;
export const getProjectName = (state: State) => state.config.projectName;

export const getProjects = createSelector(
	[getEntities],
	(entities: EntitiesState): Project[] => entities.project.projects,
);

export const getProjectIds = createSelector(
	[getIsCompanyManaged, getProjects, getProjectId],
	(isCompanyManaged, projects, projectId) =>
		isCompanyManaged ? projects.map((x) => x.id) : [projectId],
);
export const projectIdSelector = createSelector([getProjectIds], (projectIds) => projectIds[0]);
export const getProjectKey = (state: State) => state.config.projectKey;
export const getSessionId = (state: State) => state.config.sessionId;

export const getProjectAris = createSelector([getProjectIds, getCloudId], (projectIds, cloudId) =>
	projectIds.map((id) =>
		createAri({
			resourceOwner: 'jira',
			cloudId,
			resourceType: 'project',
			resourceId: String(id),
		}),
	),
);

// custom fields
export const getRankCustomFieldId = (state: State) => state.config.rankCustomFieldId;
export const getStoryPointFieldId = (state: State) => state.config.storyPointFieldId;
export const getSprintFieldId = (state: State) => state.config.sprintFieldId;
export const getEpicLinkFieldId = (state: State) => state.config.epicLinkField?.id;
export const getEpicIssueType = (state: State) => state.config.epicIssueType ?? null;

export const getRequestColumnMigration = (state: State) => state.config.requestColumnMigration;

// configs
export const getName = (state: State) => state.config.name;
export const getBacklogStrategy = (state: State) => state.config.backlogStrategy;
export const getTimeTrackingOptions = (state: State) => state.config.timeTrackingOptions;
export const getJqlQuery = (state: State) => state.config.jqlQuery;
export const getIsParallelSprintsEnabled = (state: State) => state.config.parallelSprints;

export const isKanbanBacklogStrategy = (state: State): boolean =>
	state.config.backlogStrategy === KANBAN_BACKLOG_STRATEGY;
export const isScrumBacklogStrategy = (state: State): boolean =>
	getBacklogStrategy(state) === SPRINT_BACKLOG_STRATEGY;

export const getKanbanBoardColumnId = (state: State) => state?.config?.kanbanBoardColumnId ?? null;
export const getKanbanBacklogColumnId = (state: State) =>
	state?.config?.kanbanBacklogColumnId ?? null;
export const getKanbanTargetColumnId = (state: State) => (cardListType: CardListType) =>
	cardListType === BACKLOG ? getKanbanBacklogColumnId(state) : getKanbanBoardColumnId(state);

export const getHasActiveSprints = (state: State): boolean => {
	// TODO: Update this to use Object.values when TS comes in
	// Damn you Flow.
	const { cardList } = getEntities(state);
	return Object.keys(cardList).some(
		(key) => cardList[key].sprintData?.state === SPRINT_STATE_ACTIVE,
	);
};

export const isCardMoveDisabled = (state: State) =>
	state.config.isCompanyManaged === true && state.config.isOrderedByRank === false;

// project types

export const isCmpKanbanProject = (state: State) =>
	getIsCompanyManaged(state) && isKanbanBacklogStrategy(state);

export const isCheckboxSelectionMode = (state: State) => state.ui.cards.isCheckboxSelectionMode;

export const isCMPScrumBacklog = (state: State) =>
	getIsCompanyManaged(state) && isScrumBacklogStrategy(state);

export type BoardType =
	| typeof CMP_KANBAN_BOARD_TYPE
	| typeof CMP_SCRUM_BOARD_TYPE
	| typeof AGILITY_BOARD_TYPE;

export const getBoardType = (state: State): BoardType => {
	if (getIsCompanyManaged(state)) {
		if (isScrumBacklogStrategy(state)) {
			return CMP_SCRUM_BOARD_TYPE;
		}
		if (isKanbanBacklogStrategy(state)) {
			return CMP_KANBAN_BOARD_TYPE;
		}
	}
	return AGILITY_BOARD_TYPE;
};

export const getBacklogExtensionData = (state: State): ForgeUiBoardData => {
	const { projectKey, projectId, rapidViewId } = state.config;
	return {
		project: {
			id: toProjectId(String(projectId)),
			key: projectKey,
			type: SOFTWARE_PROJECT,
		},
		board: {
			id: rapidViewId,
			type: getBoardType(state),
		},
	};
};
